<template>
  <Dialog ref="dialog" :title="$t('label.passwordUpdate')" :color="'next'">
    <v-container class="pt-0 px-8">
      <Form ref="observer">
        <v-form lazy-validation ref="form" @submit.prevent>
          <template v-if="isForce && isForceCreateSecretQuestion">
            <!-- 秘密の質問 -->
            <v-row>
              <v-col :class="conditionStyle">
                <Field
                  name="secretQuestion"
                  :rules="`selectRequired`"
                  v-model="secretQuestion.questionId"
                  v-slot="{ errorMessage }"
                >
                  <v-select
                    v-model="secretQuestion.questionId"
                    item-title="text"
                    item-value="value"
                    :items="SecretQuestions"
                    hide-details
                    :label="$t('label.secretQuestion')"
                  />
                  <ValidationError class="ma-0" :error="errorMessage" />
                </Field>
              </v-col>
            </v-row>
            <v-row>
              <v-col :class="conditionStyle">
                <Field
                  name="secretQuestionAnswer"
                  :rules="`required|max:${MaxLength.SecretQuestionAnswer}`"
                  v-model="secretQuestion.answer"
                  v-slot="{ errorMessage }"
                >
                  <v-text-field
                    v-model="secretQuestion.answer"
                    hide-details
                    :label="$t('label.answer')"
                  />
                  <ValidationError class="ma-0" :error="errorMessage" />
                </Field>
              </v-col>
            </v-row>
          </template>
          <!-- 現在のパスワード -->
          <v-row v-show="!isForce">
            <v-col :class="conditionStyle">
              <Field
                :rules="
                  `required|min:${MinLength.OldCognitoPassword}|max:${MaxLength.CognitoPassword}`
                "
                v-model="currentPassword"
                v-slot="{ handleChange, errorMessage }"
                name="currentPassword"
              >
                <v-text-field
                  :model-value="currentPassword"
                  hide-details
                  type="Password"
                  :label="$t('label.editUserInfo.currentPassword')"
                  @update:model-value="val => { currentPassword = val; handleChange(val, true); }"
                ></v-text-field>
                <ValidationError class="ma-0" :error="errorMessage" />
              </Field>
            </v-col>
          </v-row>
          <!-- 新しいパスワード -->
          <v-row>
            <v-col class="mt-0 mb-0 py-0">
              <Field
                :rules="
                  `required|requiredHalfNumber|requiredHalfUppercase|requiredHalfLowercase|min:${MinLength.CognitoPassword}|checkIsSameAsTheCurrentPassword:@currentPassword|max:${MaxLength.CognitoPassword}`
                "
                v-model="newPassword"
                v-slot="{ handleChange, errorMessage }"
                name="newPassword"
              >
                <v-text-field
                  :model-value="newPassword"
                  hide-details
                  type="Password"
                  :label="$t('label.editUserInfo.newPassword')"
                  @update:model-value="val => { newPassword = val; handleChange(val, true); }"
                ></v-text-field>
                <ValidationError class="ma-0" :error="errorMessage" />
              </Field>
            </v-col>
          </v-row>
          <p class="mt-1 mb-3" style="font-size: 13px;">
            {{ $t('label.passwordValidationRule') }}
          </p>
          <!-- 新しいパスワード確認 -->
          <v-row>
            <v-col :class="conditionStyle">
              <Field
                name="newPasswordConfirm"
                :rules="
                  `required|checkIsSameAsTheNewPassword:@newPassword|max:${MaxLength.CognitoPassword}`
                "
                v-model="newPasswordConfirm"
                v-slot="{ handleChange, errorMessage }"
              >
                <v-text-field
                  :model-value="newPasswordConfirm"
                  hide-details
                  type="Password"
                  :label="$t('label.editUserInfo.newPasswordConfirm')"
                  @update:model-value="val => { newPasswordConfirm = val; handleChange(val, true); }"
                ></v-text-field>
                <ValidationError class="ma-0" :error="errorMessage" />
              </Field>
            </v-col>
          </v-row>
        </v-form>
      </Form>
    </v-container>

    <template v-slot:footer>
      <v-btn width="33%" class="back" dark @click="close()">
        {{ $t('button.cancel') }}
      </v-btn>
      <v-btn width="33%" class="next" dark @click="onClickUpdateButton()">{{
        $t('button.update')
      }}</v-btn>
    </template>
    <ErrorDialog ref="errorDialog"></ErrorDialog>
  </Dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import Dialog from '@/components/organisms/agency/Dialog';
import ErrorDialog from '@/components/organisms/agency/ErrorDialog';
import { Form, Field, defineRule } from 'vee-validate';
import ValidationError from '@/components/organisms/common/ValidationError';
import { MaxLength, MinLength, SecretQuestions } from '@/lib/const';
import { validation } from '@/lib/validation';

/**
 * NOTE:  バリデーションはveeValidation.js上に共通化されているが、
 *        共通ファイル上ではターゲットを取得できなかったためこちらで定義
 */
// パスワードの同一性チェック（現在パスワードと新しいパスワード）
// vee-validate v4: @fieldName はフィールドの「値」を target に渡すため直接比較する
defineRule('checkIsSameAsTheCurrentPassword', (value, [target]) => {
  return validation.notConfirmed(value, target);
});
// パスワードの同一性チェック（新しいパスワードと新しいパスワード確認）
defineRule('checkIsSameAsTheNewPassword', (value, [target]) => {
  return validation.confirmed(value, target);
});

export default {
  components: {
    Dialog,
    ErrorDialog,
    Form,
    Field,
    ValidationError,
  },
  data() {
    return {
      // 編集可能な代理店情報項目
      currentPassword: '',
      newPassword: '',
      newPasswordConfirm: '',

      // 編集フォームのスタイル指定
      conditionStyle: ['mt-0', 'mb-4', 'py-0'],

      // 強制パスワード変更であるかどうか
      isForce: false,

      // 秘密の質問の登録が必要であるかどうか
      isForceCreateSecretQuestion: false,

      // 秘密の質問
      secretQuestion: {
        questionId: null,
        answer: null,
      },
      SecretQuestions: SecretQuestions,

      // 有効文字長
      MaxLength: MaxLength,
      MinLength: MinLength,
    };
  },
  mounted() {},
  methods: {
    ...mapGetters('user', ['userDetail']),
    open() {
      this.$refs.dialog.open();
    },
    close() {
      this.$refs.dialog.close();
    },
    init(currentPassword = '') {
      // 現在のパスワードが入力済の場合、強制パスワード変更とみなす
      if (currentPassword) this.isForce = true;

      // 秘密の質問の登録有無確認
      if (!this.userDetail().secretQuestionFlg)
        this.isForceCreateSecretQuestion = true;

      // 入力値を初期化
      this.currentPassword = currentPassword;
      this.newPassword = '';
      this.newPasswordConfirm = '';
      if (this.$refs.observer) {
        // 入力バリデーションを初期化
        this.$refs.observer.resetForm();
      }
    },
    async onClickUpdateButton() {
      // バリデーション
      const { valid } = await this.$refs.observer.validate();
      if (valid) {
        // 編集項目の保持
        const condition = {
          ...(this.currentPassword && {
            currentPassword: this.currentPassword,
          }),
          ...(this.newPassword && {
            newPassword: this.newPassword,
          }),
          ...(this.newPasswordConfirm && {
            newPasswordConfirm: this.newPasswordConfirm,
          }),
          ...(this.secretQuestion.questionId && {
            secretQuestion: this.secretQuestion,
          }),
        };
        this.$emit('onSuccess', condition);
      }
    },
    onUpdateSuccess() {
      this.$emit('onUpdateSuccess');
    },
    onErrorUpdate(message) {
      this.$refs.errorDialog.open(
        this.$t('title.agency.passwordUpdateResult'),
        message
      );
    },
  },
};
</script>
